// Creación de la suscripción de MercadoPago a partir de la tarjeta tokenizada
// en el front (CardPayment Brick → `card_token_id`).
//
// FASE FRONTEND: este helper manda el token al backend, que es quien crea el
// `POST /preapproval` (cobro inmediato → status `authorized`). Ese endpoint
// TODAVÍA NO EXISTE — queda configurable vía `MP_SUBSCRIPTION_URL`. Mientras
// no esté, en desarrollo se usa `MP_MOCK_RESULT` para poder verificar los
// estados de la UI (éxito / rechazo) sin backend.
//
// El primer resultado del cobro se devuelve INLINE (no por webhook): por eso
// la pantalla distingue `authorized` (éxito propio) de `rejected` (error +
// reintento con otra tarjeta). Los cambios de estado POSTERIORES de la
// suscripción los sigue manejando el webhook ya existente.

import {
  GENERATE_AUTH_TOKEN,
  MP_MOCK_RESULT,
  MP_SUBSCRIPTION_URL,
} from '../config';

/** Datos que devuelve `onSubmit` del CardPayment Brick (los que usa el backend). */
export interface CardFormData {
  token: string;
  payment_method_id: string;
  issuer_id?: string;
  installments?: number;
  transaction_amount?: number;
  payer?: {
    email?: string;
    identification?: { type?: string; number?: string };
  };
}

export interface CreateSubscriptionParams {
  userId: string;
  plan: string;
  /** Tokenización de la tarjeta (del Brick). */
  card: CardFormData;
  /** Monto del primer cobro, en la unidad de la moneda (ARS). */
  amount: number;
  /** Cupón aplicado, opcional. */
  couponId?: string | null;
  withDiscount?: boolean;
}

export type SubscriptionStatus = 'authorized' | 'rejected' | 'error';

export interface CreateSubscriptionResult {
  status: SubscriptionStatus;
  /** Mensaje legible para mostrar inline en caso de rechazo/error. */
  message?: string;
  /** Respuesta cruda del backend (para debug). */
  raw?: unknown;
}

// Mensaje por defecto cuando MP rechaza la tarjeta en el primer cobro.
const DEFAULT_REJECT_MESSAGE =
  'No pudimos procesar la tarjeta. Revisá los datos o probá con otra.';

/**
 * Manda el `card_token_id` (+ plan, cupón) al backend para crear la
 * suscripción. Normaliza la respuesta a `{ status }`.
 *
 * - `authorized` → la suscripción quedó activa (primer cobro OK).
 * - `rejected`   → MP rechazó la tarjeta en el primer cobro (mostrar inline,
 *                  permitir reintento con otra tarjeta).
 * - lanza error  → falla de red / endpoint no configurado (lo maneja la UI).
 */
export async function createMpSubscription(
  params: CreateSubscriptionParams,
): Promise<CreateSubscriptionResult> {
  const { userId, plan, card, amount, couponId, withDiscount } = params;

  // ── Mock de desarrollo (sin backend) ────────────────────────────────────
  // Solo si: estamos en DEV, no hay endpoint configurado y hay un mock seteado.
  if (import.meta.env.DEV && !MP_SUBSCRIPTION_URL && MP_MOCK_RESULT) {
    await new Promise((r) => setTimeout(r, 1200)); // simula latencia del cobro
    if (MP_MOCK_RESULT === 'rejected') {
      return { status: 'rejected', message: DEFAULT_REJECT_MESSAGE };
    }
    return { status: 'authorized', raw: { mock: true } };
  }

  if (!MP_SUBSCRIPTION_URL) {
    throw new Error(
      'Falta configurar el endpoint de suscripción (VITE_MP_SUBSCRIPTION_URL).',
    );
  }

  const body = {
    user_id: userId,
    plan,
    card_token_id: card.token,
    payment_method_id: card.payment_method_id,
    issuer_id: card.issuer_id,
    installments: card.installments ?? 1,
    transaction_amount: amount,
    payer: card.payer,
    ...(withDiscount && couponId
      ? { con_descuento: true, cupon_id: couponId }
      : {}),
  };

  const res = await fetch(MP_SUBSCRIPTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });

  // El backend devuelve 400 cuando MP rechaza el cobro (ver doc de Bricks):
  // { type: 'payment_authorization_error', message: '...' }.
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* respuesta sin body JSON */
  }

  if (!res.ok) {
    // Rechazo de la tarjeta vs. error genérico del servidor.
    const isAuthError =
      res.status === 400 || data?.type === 'payment_authorization_error';
    return {
      status: isAuthError ? 'rejected' : 'error',
      message: data?.message || DEFAULT_REJECT_MESSAGE,
      raw: data,
    };
  }

  // OK (2xx). Soportamos dos convenciones de respuesta:
  //  • Nativa de MP: { status: 'authorized' | 'rejected', status_detail, ... }
  //  • Convención de los webhooks de este repo: { response: 'success' | 'error' }
  const rawStatus = String(data?.status ?? data?.mp_status ?? '').toLowerCase();
  const statusDetail = String(data?.status_detail ?? '').toLowerCase();
  const responseFlag = String(data?.response ?? '').toLowerCase();

  // Rechazo EXPLÍCITO del primer cobro (puede llegar incluso con HTTP 200).
  const isRejected =
    rawStatus === 'rejected' ||
    responseFlag === 'error' ||
    data?.type === 'payment_authorization_error' ||
    statusDetail.startsWith('cc_rejected') ||
    statusDetail.startsWith('rejected');
  if (isRejected) {
    return {
      status: 'rejected',
      message: data?.message || DEFAULT_REJECT_MESSAGE,
      raw: data,
    };
  }

  // Éxito EXPLÍCITO. FAIL-CLOSED: en un flujo de pago NO declaramos éxito sin
  // una confirmación clara (si el webhook respondiera 200 vacío/ambiguo, no
  // queremos mostrar "suscripción activa" en falso).
  const isAuthorized =
    rawStatus === 'authorized' ||
    rawStatus === 'active' ||
    rawStatus === 'pending' || // creada; el webhook finaliza el estado
    responseFlag === 'success' ||
    !!data?.preapproval_id;
  if (isAuthorized) {
    return { status: 'authorized', raw: data };
  }

  // 2xx pero sin marcador (ej. body vacío) → no confirmamos el cobro.
  return {
    status: 'error',
    message: 'No pudimos confirmar el pago. Probá de nuevo en un momento.',
    raw: data,
  };
}
